import React from 'react';
import {
  Campaign,
  HelpLink,
  StepChecklist,
  StepKind,
  StepQuestion,
  campaignTheme,
  isPrimaryReportRow,
  stepKindLabel,
  trackLabel,
} from '../../../../support/campaigns';
import {
  useCampaignAssets,
  useCampaignCapabilities,
  useCampaignContentBlocks,
  useCampaignPhases,
  useCampaignPrompts,
  useCustomerPersonas,
  useReportFigureConfig,
  useReportSectionConfig,
} from '../../../../hooks/campaign-hooks';
import {
  Block,
  CampaignRoot,
  CourseCard,
  MetaTag,
  PhaseTag,
  StatusPill,
  TagRow,
  cx,
  ui,
} from '../../../Campaigns/campaignUi';
import { STEPS } from '../../../Campaigns/guided/config/steps';
import { resolveStepDef } from '../../../Campaigns/guided/config/resolveSteps';
import { StepBodyRenderer } from '../../../Campaigns/guided/composition/layout/StepBodyRenderer';
import type { RunnerContext } from '../../../Campaigns/guided/composition/RunnerContext';
import { emptyWorkshopState } from '../../../Campaigns/guided/api/emptyState';
import { setRunnerData } from '../../../Campaigns/guided/api';
import type {
  RunnerAsset,
  RunnerCapability,
  RunnerContentBlock,
  RunnerDataContext,
  RunnerPhase,
  RunnerPrompt,
  RunnerReportFigure,
  RunnerReportSection,
  RunnerStepContent,
} from '../../../Campaigns/guided/api';
import {
  setWorkshopPreviewMode,
  useWorkshopStore,
} from '../../../Campaigns/guided/store/workshopStore';
import type { RunnerContentValues } from './RunnerContentFields';
import { OPTIONAL_CONTENT_SECTIONS } from './stepContentCategories';

/**
 * Live read-only preview of a step, rendered in the campaign builder's step modal. It reuses the
 * runner's scoped design system (`CampaignRoot` + primitives) and the campaign's theme, so the admin
 * sees the step exactly as the SE gets it — updating as they type.
 *
 * Two rendering paths, chosen by whether the step adopts a content template (`runner_kind`):
 *  - A content-template step renders the REAL runner composition (`StepBodyRenderer` + the registered
 *    primitives) against the campaign's fetched DB content plus the step's draft form values. This is
 *    the same code path the runner uses, so the preview shows every configured element (the composed
 *    step-layout primitives, the seeded content blocks, personas, capabilities, assets, …).
 *  - Every other step kind (which has no composition primitives or seeded `step_layout` yet) keeps a
 *    representative approximation of its configured elements (checklist, questions, journey-map ref, …).
 *
 * The composition mount is strictly NON-PERSISTING: it primes the runner's module-singleton with a
 * throwaway `RunnerDataContext`, hydrates the workshop store with a synthetic `WorkshopState`, and puts
 * the store in preview mode (`setWorkshopPreviewMode`) so a primitive interaction can never fire the
 * enrollment PUT or the report's `localStorage` mirror. The admin surface never mounts the live runner,
 * so priming the singleton here cannot collide with a real workshop; both are cleared on unmount.
 */

export interface StepPreviewValues {
  name: string;
  description: string;
  track: string;
  step_kind: StepKind;
  runner_kind: string;
  runner: RunnerContentValues;
  help_links: HelpLink[];
  checklist: StepChecklist;
  questions: StepQuestion[];
  layout: string;
  launch_url: string;
  /** Opt-in optional content sections enabled on this step (keys into `OPTIONAL_CONTENT_SECTIONS`). */
  content_sections: string[];
}

/** A stable, obviously-synthetic id the preview keys its throwaway workshop state under. */
const PREVIEW_WORKSHOP_ID = '__campaign-preview__';

const HelpLinks: React.FC<{ links: HelpLink[] }> = ({ links }) => {
  const shown = (links ?? []).filter((l) => l && l.url.trim());
  if (!shown.length) return null;
  return (
    <div className={ui.helpLinks}>
      {shown.map((l, i) => (
        <a
          key={`${l.url}-${i}`}
          className={ui.helpLink}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.preventDefault()}
        >
          <span className={ui.helpLinkDot} aria-hidden="true" />
          {l.label.trim() || l.url.trim()}
        </a>
      ))}
    </div>
  );
};

/** Build the report-canvas grid placement from a row's nullable `layout_*` columns (see CampaignStepPage). */
function gridLayoutOf(row: {
  layout_x: number | null;
  layout_y: number | null;
  layout_w: number | null;
  layout_h: number | null;
}): { x: number; y: number; w: number; h: number } | undefined {
  const { layout_x: x, layout_y: y, layout_w: w, layout_h: h } = row;
  if (x == null || y == null || w == null || h == null) return undefined;
  return { x, y, w, h };
}

/** Map the step's draft `runner` form values to a `RunnerStepContent` entry (only non-blank fields, so a
 * blank field falls back to the bundled default exactly as the saved `metadata.runner` would). */
function draftStepContent(kind: RunnerContext['step']['kind'], values: StepPreviewValues): RunnerStepContent {
  const f = values.runner;
  const entry: RunnerStepContent = { kind };
  if (f.tag?.trim()) entry.tag = f.tag.trim();
  if (f.title?.trim()) entry.title = f.title.trim();
  if (f.subtitle?.trim()) entry.subtitle = f.subtitle.trim();
  if (f.cta?.trim()) entry.cta = f.cta.trim();
  if (typeof f.liveEligible === 'boolean') entry.liveEligible = f.liveEligible;
  const prompts = (f.quickPrompts ?? []).map((p) => p.trim()).filter(Boolean);
  if (prompts.length) entry.quickPrompts = prompts;
  if (f.guideTitle?.trim()) entry.guideTitle = f.guideTitle.trim();
  if (f.guideBody?.trim()) entry.guideBody = f.guideBody.trim();
  if (f.sectionHeadings && Object.keys(f.sectionHeadings).length) entry.sectionHeadings = f.sectionHeadings;
  const links = (values.help_links ?? []).filter((l) => l && l.url.trim());
  if (links.length) entry.helpLinks = links;
  return entry;
}

/**
 * Renders the REAL runner composition for a content-template step. Fetches the campaign's DB content
 * (the same rows CampaignStepPage feeds the live runner), maps them to the runner's decoupled shapes,
 * primes the runner data singleton, and mounts `StepBodyRenderer`. Non-persisting (see file header).
 */
const LiveStepComposition: React.FC<{ campaign: Campaign; values: StepPreviewValues }> = ({
  campaign,
  values,
}) => {
  const campaignId = campaign.id;

  // The same campaign rows CampaignStepPage fetches for the live runner. `.data` is the raw
  // react-query result (undefined until loaded); the maps below hidden-filter and shape them.
  const { data: personaRows } = useCustomerPersonas(campaignId);
  const { data: reportSectionRows } = useReportSectionConfig(campaignId);
  const { data: reportFigureRows } = useReportFigureConfig(campaignId);
  const { data: capabilityRows } = useCampaignCapabilities(campaignId);
  const { data: contentBlockRows } = useCampaignContentBlocks(campaignId);
  const { data: assetRows } = useCampaignAssets(campaignId);
  const { data: phaseRows } = useCampaignPhases(campaignId);
  const { data: promptRows } = useCampaignPrompts(campaignId);

  const personas = React.useMemo(
    () => (personaRows ?? []).filter((p) => !p.hidden).map((p) => ({ label: p.label, role: p.role, why: p.why })),
    [personaRows],
  );
  const reportSections = React.useMemo<RunnerReportSection[]>(
    () =>
      (reportSectionRows ?? [])
        .filter(isPrimaryReportRow)
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((sec) => ({
          sectionId: sec.section_id,
          title: sec.title,
          order: sec.order,
          source: sec.source,
          isDataSection: sec.is_data_section,
          hiddenByDefault: sec.hidden_by_default,
          questionId: sec.ai_question ?? undefined,
          blocks: sec.blocks,
          preloadedContent: sec.preloaded_content || undefined,
          aiEnabled: sec.ai_enabled,
          layout: gridLayoutOf(sec),
        })),
    [reportSectionRows],
  );
  const reportFigures = React.useMemo<RunnerReportFigure[]>(
    () =>
      (reportFigureRows ?? [])
        .filter(isPrimaryReportRow)
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((fig) => ({
          id: fig.id,
          figureId: fig.figure_id,
          sectionId: fig.section_id,
          kind: fig.kind,
          order: fig.order,
          anchor: fig.anchor,
          title: fig.title,
          caption: fig.caption,
          explanation: fig.explanation,
          chartKind: fig.chart_kind,
          tone: fig.tone,
          chartQuery: fig.chart_query,
          imageObjectKey: fig.image_object_key,
          imageWidth: fig.image_width,
          imageHeight: fig.image_height,
          layout: gridLayoutOf(fig),
        })),
    [reportFigureRows],
  );
  const capabilities = React.useMemo<RunnerCapability[]>(
    () =>
      (capabilityRows ?? [])
        .filter((c) => !c.hidden)
        .map((c) => ({
          key: c.key,
          name: c.name,
          description: c.description,
          detail: c.detail,
          proposal: c.proposal,
          resourceLinks: Array.isArray(c.resource_links) ? c.resource_links : [],
        })),
    [capabilityRows],
  );
  const contentBlocks = React.useMemo<RunnerContentBlock[]>(
    () =>
      (contentBlockRows ?? [])
        .filter((b) => !b.hidden)
        .map((b) => ({
          id: b.id,
          parentId: b.parent ?? null,
          category: b.category,
          key: b.key,
          label: b.label,
          order: b.order,
          payload: (b.payload ?? {}) as Record<string, any>,
        })),
    [contentBlockRows],
  );
  const assets = React.useMemo<RunnerAsset[]>(
    () =>
      (assetRows ?? [])
        .filter((a) => !a.hidden)
        .map((a) => ({
          id: a.id,
          key: a.key,
          kind: a.kind,
          title: a.title,
          objectKey: a.object_key,
          contentType: a.content_type,
          metadata: (a.metadata ?? {}) as Record<string, any>,
        })),
    [assetRows],
  );
  const phases = React.useMemo<RunnerPhase[]>(
    () =>
      (phaseRows ?? [])
        .filter((p) => !p.hidden)
        .map((p) => ({ key: p.key, label: p.label, order: p.order, metadata: (p.metadata ?? {}) as Record<string, any> })),
    [phaseRows],
  );
  const prompts = React.useMemo<RunnerPrompt[]>(
    () =>
      (promptRows ?? [])
        .filter((p) => !p.hidden)
        .map((p) => ({
          flowKey: p.flow_key,
          label: p.label,
          template: p.template,
          outputSchema: (p.output_schema ?? {}) as Record<string, any>,
          model: p.model ?? null,
          groundingInputs: (p.grounding_inputs ?? []) as string[],
          metadata: (p.metadata ?? {}) as Record<string, any>,
        })),
    [promptRows],
  );

  // The bundled step index for this content template — its `kind` matches the seeded `step_layout`
  // block, so `resolveStepLayout(ctx.step)` returns the authored primitives.
  const stepIndex = React.useMemo(
    () => STEPS.findIndex((s) => s.kind === values.runner_kind),
    [values.runner_kind],
  );

  // The step's draft copy overrides the bundled/DB content for the previewed kind, so header edits show
  // live. Merged into the primed `stepContent` array (its `resolveStepDef` lookup keys off `kind`).
  const previewCtx = React.useMemo<RunnerDataContext | null>(() => {
    if (stepIndex < 0) return null;
    return {
      campaignId,
      enrollmentId: PREVIEW_WORKSHOP_ID,
      workshopId: PREVIEW_WORKSHOP_ID,
      record: { id: PREVIEW_WORKSHOP_ID, metadata: {} },
      personas,
      reportSections,
      reportFigures,
      stepContent: [draftStepContent(STEPS[stepIndex].kind, values)],
      capabilities,
      contentBlocks,
      assets,
      phases,
      prompts,
    };
  }, [
    campaignId,
    stepIndex,
    values,
    personas,
    reportSections,
    reportFigures,
    capabilities,
    contentBlocks,
    assets,
    phases,
    prompts,
  ]);

  // Prime the singleton DURING render (mirrors GuidedRunner) so `StepBodyRenderer`'s `resolveStepLayout`
  // reads the authored layout on the first paint rather than an empty singleton. `setRunnerData` only
  // mutates a module variable — it triggers no React update — so it must run here, not in an effect.
  React.useMemo(() => {
    if (previewCtx) setRunnerData(previewCtx);
  }, [previewCtx]);

  // Non-persist lifecycle: preview mode + a synthetic state on mount; both torn down (and the singleton
  // cleared — the admin surface never has a live runner singleton to restore) on unmount.
  React.useEffect(() => {
    setWorkshopPreviewMode(true);
    useWorkshopStore.getState().hydrate(emptyWorkshopState(PREVIEW_WORKSHOP_ID));
    return () => {
      useWorkshopStore.getState().clear();
      setWorkshopPreviewMode(false);
      setRunnerData(null);
    };
  }, []);

  const state = useWorkshopStore((st) => st.state);

  if (stepIndex < 0) {
    return (
      <Block title="Unknown content template" lead="This step's content template isn't recognised, so there's nothing to preview.">
        <span />
      </Block>
    );
  }
  if (!state) {
    return (
      <div className={cx(ui.card, ui.contentCard)} style={{ padding: '60px 24px', textAlign: 'center' }}>
        Loading preview…
      </div>
    );
  }

  const step = resolveStepDef(STEPS[stepIndex]);
  const runnerContext: RunnerContext = {
    step,
    stepIndex,
    state,
    workshopId: PREVIEW_WORKSHOP_ID,
    workshop: undefined,
    customer: null,
    insights: undefined,
    customerName: 'this account',
    author: 'Preview',
    presenting: false,
  };

  const prompts_ = step.quickPrompts ?? [];
  return (
    <div className={cx(ui.card, ui.contentCard)}>
      <div style={{ marginBottom: 12 }}>
        <PhaseTag>{step.tag}</PhaseTag>
      </div>
      <h2 style={{ margin: '0 0 8px' }}>{step.title}</h2>
      {step.subtitle && <p style={{ opacity: 0.85, marginTop: 0 }}>{step.subtitle}</p>}
      <HelpLinks links={step.helpLinks ?? []} />
      {step.liveEligible && (
        <div style={{ margin: '12px 0' }}>
          <StatusPill tone="progress" label="Go Live: present full-screen" />
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <StepBodyRenderer ctx={runnerContext} />
      </div>
      {prompts_.length > 0 && (
        <Block title="Workshop Guide — quick prompts">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {prompts_.map((p, i) => (
              <MetaTag key={i}>{p}</MetaTag>
            ))}
          </div>
        </Block>
      )}
    </div>
  );
};

/** A representative body for a non-template step kind (no composition primitives / seeded layout yet). */
const KindBody: React.FC<{ values: StepPreviewValues }> = ({ values }) => {
  const items = (values.checklist?.items ?? []).filter((it) => it && it.label);
  switch (values.step_kind) {
    case 'journey_map':
      return (
        <CourseCard
          title={values.name || 'Course'}
          description={values.description || null}
          href="#"
          ctaLabel="Start course ↗"
        />
      );
    case 'assessment':
      return (
        <Block title="Assessment" lead="The SE completes the linked assessment; completion is tracked from it.">
          <StatusPill tone="notstarted" />
        </Block>
      );
    case 'moodle_workshop':
      return (
        <CourseCard
          title={values.name || 'Workshop'}
          description={values.description || null}
          href={values.launch_url || '#'}
          ctaLabel="Open workshop ↗"
        />
      );
    case 'group':
      return (
        <Block title="Group" lead={`Contains sub-steps, laid out ${values.layout === 'grid-3' ? 'three across' : values.layout === 'grid-2' ? 'two across' : 'stacked'}.`}>
          <div className={values.layout === 'grid-3' ? ui.childGrid3 : values.layout === 'grid-2' ? ui.childGrid : undefined}>
            <MetaTag>Sub-step 1</MetaTag>
            <MetaTag>Sub-step 2</MetaTag>
            {values.layout === 'grid-3' && <MetaTag>Sub-step 3</MetaTag>}
          </div>
        </Block>
      );
    case 'question':
      return (
        <Block title="Questions">
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {(values.questions ?? []).map((q, i) => (
              <li key={q.key || i} style={{ marginBottom: 6 }}>
                {q.label || <em>Untitled question</em>}
              </li>
            ))}
            {(values.questions ?? []).length === 0 && <li><em>No questions authored yet.</em></li>}
          </ol>
        </Block>
      );
    case 'salesforce_link':
      return (
        <Block title="Salesforce link" lead="The SE picks a Salesforce account (and optionally an opportunity) here in the runner." >
          <StatusPill tone="notstarted" />
        </Block>
      );
    default:
      // content / action_item / followup — a self-attest checklist when one is authored.
      if (items.length) {
        return (
          <Block title={values.checklist?.lead || 'Checklist'}>
            <div className={values.layout === 'grid-2' ? ui.checklistGrid : undefined}>
              {items.map((it, i) => (
                <div key={it.key || i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span aria-hidden="true">☐</span>
                  <span>{it.label}</span>
                </div>
              ))}
            </div>
          </Block>
        );
      }
      return values.description ? (
        <Block title="Details">
          <p style={{ margin: 0 }}>{values.description}</p>
        </Block>
      ) : (
        <Block title="Details" lead="This step renders its name, description and any help links." >
          <span />
        </Block>
      );
  }
};

/**
 * Representative render of the opt-in optional content sections (WS3 — e.g. FAQ) enabled on a content
 * step. The actual entries are campaign-wide content blocks saved independently, so here we show the
 * section's heading + where it lands. Renders nothing when no sections are enabled.
 */
const OptionalSections: React.FC<{ enabled: string[] }> = ({ enabled }) => {
  const sections = OPTIONAL_CONTENT_SECTIONS.filter((s) => (enabled ?? []).includes(s.category));
  if (!sections.length) return null;
  return (
    <>
      {sections.map((s) => (
        <div key={s.category} style={{ marginTop: 12 }}>
          <Block title={s.title} lead={`Optional “${s.label}” section. Its entries come from the campaign’s “${s.category}” content blocks.`}>
            <span style={{ opacity: 0.7 }}>
              Rendered by the <code>{s.primitive}</code> primitive when placed in this step’s layout.
            </span>
          </Block>
        </div>
      ))}
    </>
  );
};

export function StepPreview(props: { campaign: Campaign; values: StepPreviewValues }): JSX.Element {
  const { campaign, values } = props;
  const isTemplated = !!values.runner_kind;
  return (
    <CampaignRoot theme={campaignTheme(campaign)}>
      {isTemplated ? (
        <LiveStepComposition campaign={campaign} values={values} />
      ) : (
        <div className={cx(ui.card, ui.contentCard)}>
          <TagRow>
            <PhaseTag>{trackLabel(values.track)}</PhaseTag>
            <MetaTag>{stepKindLabel(values.step_kind)}</MetaTag>
          </TagRow>
          <h2 style={{ margin: '4px 0 8px' }}>{values.name || 'Untitled step'}</h2>
          {values.description && <p style={{ opacity: 0.85, marginTop: 0 }}>{values.description}</p>}
          <HelpLinks links={values.help_links} />
          <div style={{ marginTop: 12 }}>
            <KindBody values={values} />
          </div>
          {values.step_kind === 'content' && <OptionalSections enabled={values.content_sections} />}
        </div>
      )}
    </CampaignRoot>
  );
}

export default StepPreview;
